import type { IsoTimestamp, TimeOfDay } from '~/types/dates'
import { timeOfDayFrom, toIsoTimestamp, toTimeOfDay, unwrapTime } from '~/types/dates'
import type { RoutineAnchorId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssue, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * Routine anchors (D-099, as amended at C6.5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  AN ANCHOR CANNOT NOTIFY ANYONE, AND THE COPY MUST NEVER IMPLY THAT IT CAN
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  iOS supports Web Push for an installed PWA and the D-032 baseline is iOS 17,
 *  so the *capability* exists. What does not exist is anywhere to push **from**:
 *  Web Push needs a service holding a subscription and sending on a schedule,
 *  Project Sola has no backend (D-088), and the Sola-operated Worker is D-095
 *  and unwritten.
 *
 *  **So an anchor changes what the app shows when the user opens it, and
 *  nothing else.** The first person to read "remind me at 07:30" and receive
 *  nothing at 07:30 will conclude the feature is broken, and they will be right
 *  about the promise. Every string in this file and its screens is checked
 *  against that by a test — see `ANCHOR_COPY` below.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  AN ANCHOR IS A PREFERENCE, NEVER EVIDENCE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  No rule reads one, nothing is judged against one, and **a missed anchor
 *  writes nothing at all**. That is what keeps it outside D-060's converse: it
 *  is not data the engine wants and does not have, it is a display setting.
 *
 *  Deferral is **one replaceable `suppressedUntil` per anchor and never a
 *  history** — no dismissal count, no missed-reminder history, no reason kept
 *  for analysis. The anti-streak rule forbids accumulating and evaluating
 *  misses; it does not forbid remembering the user's current instruction to stop
 *  asking, which is what this field is.
 */

export const ROUTINE_ANCHOR_KINDS = [
  'weighIn',
  'measurement',
  'photos',
  'weeklyReview',
  'strengthUpdate',
  'backup',
] as const

export type RoutineAnchorKind = (typeof ROUTINE_ANCHOR_KINDS)[number]

export const ROUTINE_ANCHOR_LABELS: Readonly<Record<RoutineAnchorKind, string>> = {
  weighIn: 'Weigh-in',
  measurement: 'Measurements',
  photos: 'Progress photos',
  weeklyReview: 'Weekly review',
  strengthUpdate: 'Strength update',
  backup: 'Backup',
}

/**
 * How often the underlying task comes round.
 *
 * **Not a new threshold and not a new cadence** — each of these already exists
 * and was already approved. It is recorded here so that eligibility can ask "is
 * this task outstanding?" without every caller restating the answer.
 *
 * Without it, an anchor is eligible from its start time to the end of *every*
 * day, so a measurement anchor at 08:00 would prompt daily for a weekly task.
 * That is the nagging D-099's copy constraint exists to prevent, arriving
 * through the schedule instead of through the wording.
 */
export const ROUTINE_ANCHOR_CADENCE: Readonly<
  Record<RoutineAnchorKind, 'daily' | 'weekly' | 'fortnightly'>
> = {
  weighIn: 'daily',
  measurement: 'weekly',
  photos: 'weekly',
  weeklyReview: 'weekly',
  strengthUpdate: 'weekly',
  backup: 'fortnightly',
}

export function isRoutineAnchorKind(value: string): value is RoutineAnchorKind {
  return (ROUTINE_ANCHOR_KINDS as readonly string[]).includes(value)
}

/**
 * The id is derived from the kind, so one anchor per kind is structural.
 *
 * The `kind` index is **not unique** and the schema is frozen (D-092), so two
 * `weighIn` anchors would otherwise be perfectly representable — and suppressing
 * one would leave the other prompting, which reads as the snooze button being
 * broken. Deriving the id makes the second one an overwrite rather than a
 * duplicate. Same reasoning as `connections`' one-per-provider, and the same
 * derived-id reasoning as `backfillProject.ts`.
 */
export function routineAnchorId(kind: RoutineAnchorKind): RoutineAnchorId {
  return `routine-anchor:${kind}` as RoutineAnchorId
}

export interface RoutineAnchorRecord extends RecordEnvelope {
  readonly id: RoutineAnchorId
  readonly kind: RoutineAnchorKind
  /**
   * **A string, never a boolean.**
   *
   * IndexedDB has no boolean key type: a record holding `true` at an indexed
   * property is not rejected, it is silently left out of the index. The v1
   * schema shipped that latent defect on `sentinelLifts.active`, and the v3
   * `keyRationale` for this store records the hazard in advance.
   */
  readonly enabled: 'yes' | 'no'
  /**
   * When the window opens. Absent is the explicit "no time set" state.
   *
   * An anchor with no time is never eligible to prompt. It is a real state
   * rather than a missing value — the user may enable a kind before deciding
   * when, and the screen says so instead of picking one for them.
   */
  readonly startTime?: TimeOfDay
  /**
   * The current instruction to stop asking. **One value, replaced, never a list.**
   *
   * Cleared once it has passed. Explicitly not stored beside it: how many times
   * the user has deferred, when they previously did, or why.
   */
  readonly suppressedUntil?: IsoTimestamp
}

export interface RoutineAnchorInput {
  readonly kind: string
  readonly enabled: string
  readonly startTime?: string
}

export function validateRoutineAnchor(input: RoutineAnchorInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (!isRoutineAnchorKind(input.kind)) {
    issues.push(issue('kind', `A routine time must be one of: ${ROUTINE_ANCHOR_KINDS.join(', ')}.`))
  }

  if (input.enabled !== 'yes' && input.enabled !== 'no') {
    issues.push(issue('enabled', 'A routine time is either on or off.'))
  }

  if (input.startTime !== undefined && toTimeOfDay(input.startTime) === undefined) {
    issues.push(issue('startTime', 'A start time must be a time of day, such as 07:30.'))
  }

  return issues
}

/**
 * Build a validated anchor.
 *
 * `existing` carries the suppression forward, because saving a time is not the
 * same act as answering a prompt — editing the hour should not silently
 * un-suppress something the user asked to stop seeing today.
 */
export function buildRoutineAnchorRecord(
  input: RoutineAnchorInput,
  now: IsoTimestamp,
  existing?: RoutineAnchorRecord,
): Result<RoutineAnchorRecord, ValidationIssues> {
  const issues = validateRoutineAnchor(input)
  if (issues.length > 0) return err(issues)

  const kind = input.kind as RoutineAnchorKind
  const startTime = input.startTime === undefined ? undefined : toTimeOfDay(input.startTime)

  return ok({
    id: routineAnchorId(kind),
    kind,
    enabled: input.enabled as 'yes' | 'no',
    ...(startTime === undefined ? {} : { startTime }),
    ...(existing?.suppressedUntil === undefined
      ? {}
      : { suppressedUntil: existing.suppressedUntil }),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/**
 * How long a snooze lasts.
 *
 * A display constant, deliberately **not** in `rules/thresholds.ts`: no rule
 * reads it, nothing is judged against it, and D-099 introduces no threshold.
 * Putting it there would imply the engine has an opinion about snoozing.
 */
export const SNOOZE_MINUTES = 120

export type AnchorEligibility =
  | { readonly kind: 'off' }
  | { readonly kind: 'noTimeSet' }
  | { readonly kind: 'beforeWindow'; readonly opensAt: TimeOfDay }
  | { readonly kind: 'suppressed'; readonly until: IsoTimestamp }
  | { readonly kind: 'open'; readonly openedAt: TimeOfDay }

/**
 * Whether this anchor's window is open right now.
 *
 * **It does not know whether the task is done** — that is the service's job, and
 * keeping the two apart is what lets this stay pure and fixture-testable while
 * the "is it outstanding?" question reads six different stores.
 */
export function anchorEligibility(anchor: RoutineAnchorRecord, now: Date): AnchorEligibility {
  if (anchor.enabled !== 'yes') return { kind: 'off' }
  if (anchor.startTime === undefined) return { kind: 'noTimeSet' }

  if (anchor.suppressedUntil !== undefined && now.toISOString() < anchor.suppressedUntil) {
    return { kind: 'suppressed', until: anchor.suppressedUntil }
  }

  /* Lexicographic, which is only sound because `TimeOfDay` is validated
     zero-padded and in range. */
  if (unwrapTime(timeOfDayFrom(now)) < unwrapTime(anchor.startTime)) {
    return { kind: 'beforeWindow', opensAt: anchor.startTime }
  }

  return { kind: 'open', openedAt: anchor.startTime }
}

/**
 * Snooze: later today, and never past the end of today.
 *
 * **The clamp is load-bearing.** Without it a 23:30 snooze rolls into tomorrow
 * and becomes indistinguishable from "not today" — two controls differing in
 * name only, which is the collapse the window design exists to prevent (D-099
 * §A). Clamped, a late snooze simply means "not again tonight", which is what
 * the user meant.
 */
export function snoozeUntil(now: Date): IsoTimestamp {
  const later = new Date(now.getTime() + SNOOZE_MINUTES * 60_000)
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  return toIsoTimestamp(later < endOfToday ? later : endOfToday)
}

/**
 * Not today: until tomorrow's window opens.
 *
 * **Local calendar arithmetic, never `+ 24 hours`.** Adding milliseconds lands
 * an hour early or late across a daylight-saving boundary — on the two days a
 * year nobody tests, and in a direction that either re-prompts the same evening
 * or skips the next morning entirely. Asking the `Date` constructor for
 * "tomorrow at 07:30" gets the right instant on both.
 */
export function notTodayUntil(anchor: RoutineAnchorRecord, now: Date): IsoTimestamp {
  const [hours, minutes] = (anchor.startTime === undefined ? '00:00' : unwrapTime(anchor.startTime))
    .split(':')
    .map(Number)

  return toIsoTimestamp(
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, hours ?? 0, minutes ?? 0, 0, 0),
  )
}

/**
 * Every user-facing string this feature owns.
 *
 * Collected rather than inlined so that one test can assert the whole feature's
 * copy at once. **A prohibition that lives only in a decision record is checked
 * by whoever remembers it**; the guard in `routineAnchor.test.ts` scans these
 * for notification language, which is the only place that promise can be
 * mechanically enforced.
 */
export const ANCHOR_COPY = {
  regionHeading: 'Ready when you are',
  cannotNotify: 'Project Sola shows this when you open the app. It cannot interrupt you.',
  nothingOpen: 'Nothing open right now.',
  snooze: 'Snooze',
  notToday: 'Not today',
  editorHeading: 'Routine times',
  editorLede:
    'Pick a time and Project Sola will put the task at the top of Today from then on, whenever you next open the app.',
  noTimeSet: 'No time set',
  windowOpened: (label: string, at: string) =>
    `Your ${label.toLowerCase()} window opened at ${at}.`,
} as const
